interface RawSampleChartStyle {
  title: string;
  pcode: string;
  description?: string;
  symbol?: string;
  sharpChartArguments?: string;
  membersOnly?: boolean;
  shouldLoadSymbol?: boolean
};

function isRawSampleChartStyle(unknownValue: unknown): unknownValue is RawSampleChartStyle {
  return (
    typeof unknownValue === 'object' &&
    unknownValue !== null &&
    'title' in unknownValue &&
    'pcode' in unknownValue &&
    typeof unknownValue.title === 'string' &&
    typeof unknownValue.pcode === 'string' &&
    (!('description' in unknownValue) || typeof unknownValue.description === 'string') &&
    (!('symbol' in unknownValue) || typeof unknownValue.symbol === 'string') &&
    (!('sharpChartArguments' in unknownValue) || typeof unknownValue.sharpChartArguments === 'string') &&
    (!('membersOnly' in unknownValue) || typeof unknownValue.membersOnly === 'boolean') &&
    (!('shouldLoadSymbol' in unknownValue) || typeof unknownValue.shouldLoadSymbol === 'boolean')
  );
}

export interface RawSampleChartStyleGroup {
  rawSampleChartStyleGroupId: string;
  rawSampleChartStyles: RawSampleChartStyle[];
};

function parseRawSampleChartStyleGroups(responseJson: unknown): RawSampleChartStyleGroup[] {
  if (!responseJson) {
    throw new Error(`Failed to fetch sample ChartStyle groups - responseJson is falsy: ${typeof responseJson} ${responseJson}.`);
  }

  if (typeof responseJson !== 'object') {
    throw new Error(`Failed to fetch sample ChartStyle groups - responseJson is truthy but is not an object: ${typeof responseJson}.`);
  }

  const rawSampleChartStyleGroups: RawSampleChartStyleGroup[] = [];

  for (const [rawSampleChartStyleGroupKey, rawSampleChartStyleGroupValue] of Object.entries(responseJson)) {
    if (!rawSampleChartStyleGroupKey) {
      window.sc3WorkbenchLogger.warn(`Sample ChartStyle group with group id: ${rawSampleChartStyleGroupKey} is invalid - sample ChartStyle group id is an empty string.`);
      continue;
    }

    if (!rawSampleChartStyleGroupValue) {
      window.sc3WorkbenchLogger.warn(`Sample ChartStyle group with group id: ${rawSampleChartStyleGroupKey} is invalid - sample ChartStyle group value is falsy: ${typeof rawSampleChartStyleGroupValue} ${rawSampleChartStyleGroupValue}.`);
      continue;
    }

    if (typeof rawSampleChartStyleGroupValue !== 'object') {
      window.sc3WorkbenchLogger.warn(`Sample ChartStyle group with group id: ${rawSampleChartStyleGroupKey} is invalid - sample ChartStyle group value is truthy but is not an object: ${typeof rawSampleChartStyleGroupValue}.`);
      continue;
    }

    const rawSampleChartStyles: RawSampleChartStyle[] = [];

    for (const [rawSampleChartStyleKey, rawSampleChartStyleValue] of Object.entries(rawSampleChartStyleGroupValue)) {
      if (!rawSampleChartStyleKey) {
        window.sc3WorkbenchLogger.warn(`Sample ChartStyle with id: ${rawSampleChartStyleKey} in group with id: ${rawSampleChartStyleGroupKey} is invalid - sample ChartStyle id is an empty string.`);
        continue;
      }

      if (!isRawSampleChartStyle(rawSampleChartStyleValue)) {
        window.sc3WorkbenchLogger.warn(`Sample ChartStyle with id: ${rawSampleChartStyleKey} in group with id: ${rawSampleChartStyleGroupKey} is invalid - sample ChartStyle does not match interface.`);
        continue;
      }

      rawSampleChartStyles.push(rawSampleChartStyleValue);
    }

    if (!rawSampleChartStyles.length) {
      window.sc3WorkbenchLogger.warn(`Sample ChartStyle group with group id: ${rawSampleChartStyleGroupKey} is invalid - sample ChartStyle group does not contain any valid sample ChartStyles.`);
      continue;
    }

    rawSampleChartStyleGroups.push({
      rawSampleChartStyleGroupId: rawSampleChartStyleGroupKey,
      rawSampleChartStyles
    });
  }

  if (!rawSampleChartStyleGroups.length) {
    throw new Error('Failed to fetch sample ChartStyle groups - all sample ChartStyle groups are empty or invalid.');
  }

  return rawSampleChartStyleGroups;
}

export async function fetchSampleChartStyleGroups(): Promise<RawSampleChartStyleGroup[]> {
  let responseJson;

  try {
    const response = await fetch('//d.stockcharts.com/freecharts/sample/chartstyles.json', {
      method: 'GET'
    });

    responseJson = await response.json();

    window.sc3WorkbenchLogger.info('sample ChartStyle groups responseJson', responseJson);
  } catch (e) {
    window.sc3WorkbenchLogger.error('Failed to fetch sample ChartStyle groups.', e);

    throw new Error('Failed to fetch sample ChartStyle groups.', { cause: e });
  }

  return parseRawSampleChartStyleGroups(responseJson);
}
